import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IncidentSymbol } from "./incidentSymbols";

// =====================================================================
// MatchLineup — Unavailable block, ONE team's list (handoff board L7).
//
// Pure presentation, props-driven from the provider's injury array
// (type / reason / missed_matches / id / name / logo). Per row: photo,
// name (+ position letter when present), a meta line joining reason and
// missed-matches with " · ", and the type chip (Injured / Suspended /
// Questionable with the shared inline SVG).
//
// Provider type contract 0–3: 1 = injured, 2 = suspended,
// 3 = questionable; 0 / unknown renders NO chip (never a fallback
// glyph — same rule as unknown incident codes). Empty renders nothing:
// a missing reason renders no reason, missed_matches 0 renders no
// count, a missing photo renders no circle (the injury entry carries no
// shirt number to fall back to), an empty list renders no block at all.
// Never "-" / "n/a" / "Unknown".
//
// The cross-team "Unavailable" section header lives in the container
// (it spans both team columns); this block is one column of it.
//
// Mobile: rows stack full-width, the meta line truncates, the chip is
// flex-none and stays visible — nothing scrolls sideways.
// =====================================================================

const TYPE_KEYS = { 1: "injured", 2: "suspended", 3: "questionable" };
const TYPE_LABELS = {
  injured: "Injured",
  suspended: "Suspended",
  questionable: "Questionable",
};

const missedLabel = (missedMatches) => {
  const count = Number(missedMatches);
  if (!Number.isFinite(count) || count <= 0) return "";
  return count === 1 ? "1 match missed" : `${count} matches missed`;
};

const RowPhoto = ({ src }) => {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [src]);
  if (!src || errored) return null;
  return (
    <img
      src={src}
      alt=""
      onError={() => setErrored(true)}
      className="rounded-full object-cover flex-none"
      style={{ width: 24, height: 24, border: "1px solid #E2E8F0" }}
    />
  );
};

const UnavailableBlock = ({ items = [], onPlayerClick }) => {
  const navigate = useNavigate();
  if (!items.length) return null;

  const activate = (item) => {
    if (onPlayerClick) {
      onPlayerClick(item);
      return;
    }
    if (item && item.platform_player_id != null) {
      navigate(`/search/player/${item.platform_player_id}`);
    }
  };
  const canActivate = (item) =>
    !!item && (!!onPlayerClick || item.platform_player_id != null);

  return (
    <div className="flex flex-col" style={{ gap: 4 }}>
      {items.map((item, index) => {
        const typeKey = TYPE_KEYS[item.type];
        const meta = [item.reason || "", missedLabel(item.missed_matches)]
          .filter(Boolean)
          .join(" · ");
        const interactive = canActivate(item);
        const rowNav = interactive
          ? {
              role: "button",
              tabIndex: 0,
              onClick: () => activate(item),
              onKeyDown: (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  activate(item);
                }
              },
            }
          : {};
        return (
          <div
            key={item.id != null ? item.id : index}
            {...rowNav}
            className={`flex items-center hover:bg-[#F8FAFC] ${
              interactive ? "cursor-pointer" : ""
            }`}
            style={{ gap: 10, minHeight: 44, padding: "4px 8px", borderRadius: 8 }}
          >
            <RowPhoto src={item.logo} />
            <div className="flex flex-col min-w-0" style={{ flex: "1 1 auto", gap: 1 }}>
              <div className="flex items-baseline min-w-0" style={{ gap: 7 }}>
                <div
                  className="truncate min-w-0"
                  data-no-translate="true"
                  style={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}
                >
                  {item.name}
                </div>
                {item.position ? (
                  <div
                    className="flex-none"
                    data-no-translate="true"
                    style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8" }}
                  >
                    {item.position}
                  </div>
                ) : null}
              </div>
              {meta ? (
                <div
                  className="truncate"
                  style={{ fontSize: 12, color: "#64748B" }}
                >
                  {meta}
                </div>
              ) : null}
            </div>
            {typeKey ? (
              <div
                className="flex-none flex items-center"
                style={{
                  gap: 5,
                  border: "1px solid #E2E8F0",
                  background: "#F8FAFC",
                  borderRadius: 999,
                  padding: "3px 9px",
                }}
              >
                <span className="flex">
                  <IncidentSymbol type={typeKey} size={12} />
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#475569" }}>
                  {TYPE_LABELS[typeKey]}
                </span>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default UnavailableBlock;
